/**
 * Estimate driving time between two coordinates using the Haversine formula.
 */

function haversineDistanceMiles(
  lat1: number, lng1: number,
  lat2: number, lng2: number
): number {
  const R = 3958.8; // Earth radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Returns estimated travel time in minutes between two lat/lng points.
 * Applies a 1.3× road-winding factor to the straight-line distance.
 *
 * @param avgSpeedMph  Average driving speed (default 60 mph)
 * @param bufferMinutes  Additional buffer time (default 30 min)
 */
export function estimateTravelMinutes(
  fromLat: number,
  fromLng: number,
  toLat: number,
  toLng: number,
  avgSpeedMph = 60,
  bufferMinutes = 30
): number {
  const straightLine = haversineDistanceMiles(fromLat, fromLng, toLat, toLng);
  const roadDistance = straightLine * 1.3;
  const drivingMinutes = (roadDistance / avgSpeedMph) * 60;
  return Math.round(drivingMinutes + bufferMinutes);
}

/**
 * Given a datetime string like "2025-01-15T08:00" and minutes to add,
 * returns a new datetime string in the same format.
 */
export function addMinutesToDatetime(datetime: string, minutes: number): string {
  const date = new Date(datetime);
  if (isNaN(date.getTime())) return datetime;
  date.setMinutes(date.getMinutes() + minutes);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  const h = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${d}T${h}:${min}`;
}
